import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Clock, ArrowLeft, FloppyDisk, Pencil } from "@phosphor-icons/react";

const WEEKDAYS = [
  { v: 0, label: "Domingo" },
  { v: 1, label: "Segunda-feira" },
  { v: 2, label: "Terça-feira" },
  { v: 3, label: "Quarta-feira" },
  { v: 4, label: "Quinta-feira" },
  { v: 5, label: "Sexta-feira" },
  { v: 6, label: "Sábado" },
];
const WEEKDAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

function emptyForm() {
  return { id: null, name: "", active: true, timezone: "America/Sao_Paulo", slots: [] };
}

export default function CompanyWorkSchedules() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("list"); // "list" | "form"
  const [form, setForm] = useState(emptyForm());
  const [newSlot, setNewSlot] = useState({ weekday: 1, start: "", end: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/work-schedules");
      setList(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setMode("form"); };
  const openEdit = (sc) => { setForm({ ...sc, slots: [...(sc.slots || [])] }); setMode("form"); };
  const backToList = () => { setMode("list"); setForm(emptyForm()); };

  const addSlot = () => {
    const s = newSlot.start; const e = newSlot.end;
    if (!s || !e) return toast.error("Preencha início e fim");
    if (s >= e) return toast.error("Início deve ser antes do fim");
    setForm((f) => ({ ...f, slots: [...f.slots, { weekday: Number(newSlot.weekday), start: s, end: e }] }));
    setNewSlot({ weekday: newSlot.weekday, start: "", end: "" });
  };

  const removeSlot = (idx) => {
    setForm((f) => ({ ...f, slots: f.slots.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome obrigatório");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), active: !!form.active,
        timezone: form.timezone || "America/Sao_Paulo",
        slots: form.slots,
      };
      if (form.id) {
        await api.put(`/work-schedules/${form.id}`, payload);
        toast.success("Jornada atualizada");
      } else {
        await api.post("/work-schedules", payload);
        toast.success("Jornada criada");
      }
      backToList();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sc) => {
    if (!window.confirm(`Excluir jornada "${sc.name}"? Isso desvincula todos os atendentes.`)) return;
    try {
      await api.delete(`/work-schedules/${sc.id}`);
      toast.success("Jornada removida");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = list.filter((x) => x.name.toLowerCase().includes(search.toLowerCase()));

  if (mode === "form") {
    // Group slots by weekday for display
    const slotsByDay = WEEKDAYS.map((d) => ({
      ...d,
      items: form.slots
        .map((s, i) => ({ ...s, _i: i }))
        .filter((s) => Number(s.weekday) === d.v)
        .sort((a, b) => a.start.localeCompare(b.start)),
    }));

    return (
      <div className="p-8 max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Equipe</p>
            <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">
              {form.id ? "Editar" : "Nova"} Jornada de Trabalho
            </h1>
            <p className="text-sm text-[#5C5C59] mt-2">
              Defina horários e vincule aos atendentes para restringir o login fora do expediente.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <button onClick={backToList} data-testid="ws-back-btn" className="flex items-center gap-1.5 text-sm text-[#5C5C59] hover:text-[#0F4C3A] transition">
            <ArrowLeft size={16} weight="bold" /> voltar
          </button>
          <Button onClick={save} disabled={saving} data-testid="ws-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
            <FloppyDisk size={16} weight="bold" className="mr-1.5" />
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </div>

        <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="ws-active-switch" className="data-[state=checked]:bg-[#0F4C3A]" />
            <Label className="text-sm">{form.active ? "Ativo" : "Inativo (sem restrição)"}</Label>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
            <p className="text-[10px] text-[#5C5C59] mt-0.5">Identificador da jornada (ex: Comercial, Suporte, Plantão)</p>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ws-name-input" className="rounded-none mt-1" />
          </div>

          <div className="pt-2 border-t border-[#E5E5E0]">
            <Label className="text-xs uppercase tracking-wider font-semibold">Adicionar horário</Label>
            <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-end gap-2 mt-2">
              <Select value={String(newSlot.weekday)} onValueChange={(v) => setNewSlot({ ...newSlot, weekday: Number(v) })}>
                <SelectTrigger data-testid="ws-slot-weekday" className="rounded-none bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white rounded-none">
                  {WEEKDAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div>
                <div className="text-[10px] text-[#5C5C59] mb-1">das</div>
                <Input type="time" value={newSlot.start} onChange={(e) => setNewSlot({ ...newSlot, start: e.target.value })} data-testid="ws-slot-start" className="rounded-none w-28" />
              </div>
              <div>
                <div className="text-[10px] text-[#5C5C59] mb-1">às</div>
                <Input type="time" value={newSlot.end} onChange={(e) => setNewSlot({ ...newSlot, end: e.target.value })} data-testid="ws-slot-end" className="rounded-none w-28" />
              </div>
              <Button onClick={addSlot} data-testid="ws-slot-add-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                <Plus size={16} weight="bold" />
              </Button>
            </div>
          </div>

          <div className="pt-3">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2">Horários da jornada</div>
            {form.slots.length === 0 && (
              <div className="text-center text-xs text-[#5C5C59] p-6 border border-dashed border-[#E5E5E0]">
                Nenhum horário. Sem horários, o atendente será bloqueado 100% do tempo.
              </div>
            )}
            <div className="space-y-1" data-testid="ws-slots-list">
              {slotsByDay.map((d) => d.items.length > 0 && (
                <div key={d.v} className="flex items-center gap-3 py-1.5 border-b border-[#E5E5E0]">
                  <div className="w-32 text-xs font-semibold uppercase tracking-wider text-[#0F4C3A]">{d.label}</div>
                  <div className="flex flex-wrap gap-2 flex-1">
                    {d.items.map((s) => (
                      <div key={s._i} className="inline-flex items-center gap-2 bg-[#EDF3F0] px-2.5 py-1 text-xs font-mono">
                        {s.start} → {s.end}
                        <button onClick={() => removeSlot(s._i)} data-testid={`ws-slot-remove-${s._i}`} className="text-[#DC2626] hover:opacity-70">
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-[#E5E5E0]">
            <Label className="text-xs uppercase tracking-wider font-semibold">Fuso horário</Label>
            <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="ws-timezone-input" className="rounded-none mt-1 font-mono text-xs" placeholder="America/Sao_Paulo" />
            <p className="text-[10px] text-[#5C5C59] mt-1">Ex: America/Sao_Paulo, America/Manaus, America/Rio_Branco.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Equipe</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Jornada de Trabalho</h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          Defina diferentes horários de trabalho e vincule aos seus colaboradores, impedindo que eles utilizem o painel fora do horário estipulado.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar…" data-testid="ws-search-input" className="rounded-none max-w-md" />
        <Button onClick={openNew} data-testid="ws-add-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
          <Plus size={16} weight="bold" className="mr-1.5" /> Adicionar
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="ws-list">
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center px-4 py-2 bg-[#F7F7F5] border-b border-[#E5E5E0] text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">
          <div>Nome</div>
          <div className="pr-4">Atendentes</div>
          <div className="pr-4">Horários</div>
          <div>Ação</div>
        </div>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhuma jornada cadastrada.</div>
        )}
        {filtered.map((sc) => (
          <div key={sc.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-4 py-3 border-b border-[#E5E5E0] last:border-b-0" data-testid={`ws-row-${sc.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Clock size={16} weight="duotone" className={sc.active ? "text-[#0F4C3A]" : "text-[#5C5C59]"} />
              <span className="font-semibold text-sm truncate">{sc.name}</span>
              {!sc.active && <span className="text-[9px] uppercase tracking-widest bg-[#F7F7F5] text-[#5C5C59] px-1.5 py-0.5">inativa</span>}
            </div>
            <div className="text-xs text-[#5C5C59] pr-4">{sc.attendants_count} atend.</div>
            <div className="text-xs text-[#5C5C59] pr-4 font-mono">{sc.slots?.length || 0} slot(s)</div>
            <div className="flex items-center gap-1">
              <button onClick={() => openEdit(sc)} data-testid={`ws-edit-${sc.id}`} className="p-1.5 text-[#0F4C3A] hover:bg-[#EDF3F0] transition">
                <Pencil size={16} weight="bold" />
              </button>
              <button onClick={() => remove(sc)} data-testid={`ws-delete-${sc.id}`} className="p-1.5 text-[#DC2626] hover:bg-[#F7F7F5] transition">
                <Trash size={16} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-5 bg-[#F7F7F5] border-l-[3px] border-[#0F4C3A]">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-[#0F4C3A]">Como funciona</p>
        <ul className="mt-2 space-y-1.5 text-sm text-[#1A1A1A]">
          <li>1. Crie uma jornada (ex: <b>Comercial</b>, seg-sex das 08:00 às 18:00)</li>
          <li>2. Vá em <b>Equipe</b> → edite o atendente e selecione a jornada</li>
          <li>3. O atendente só consegue fazer login dentro do horário definido</li>
          <li>4. Quando o expediente terminar, ele é <b>deslogado automaticamente</b> (verificação a cada 1 min)</li>
          <li>5. Dias sem horário cadastrado = bloqueado (ex: fins de semana sem slot)</li>
        </ul>
      </div>
    </div>
  );
}
